// Copyright (c) 2021-22, iPivot, Corp.
'use strict'

import { createGlobalStyle } from 'styled-components'

export const GlobalStyles = createGlobalStyle`
    :root {
        --bs-font-sans-serif: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
    }

    body {
        background: ${props => props.theme.colors.body.background} !important;
        color: ${props => props.theme.colors.body.color} !important;
        font-size: 14px !important;

        .text-primary {
            color: ${props => props.theme.colors.text.primary.color} !important;
        }

        .text-secondary {
            color: ${props => props.theme.colors.text.secondary.color} !important;
        }

        .text-success {
            color: ${props => props.theme.colors.text.success.color} !important;
        }

        .text-danger {
            color: ${props => props.theme.colors.text.danger.color} !important;
        }

        .text-warning {
            color: ${props => props.theme.colors.text.warning.color} !important;
        }

        .text-info {
            color: ${props => props.theme.colors.text.info.color} !important;
        }

        .text-muted {
            color: ${props => props.theme.colors.text.muted.color} !important;
        }

        .valid-feedback {
            color: ${props => props.theme.colors.text.success.color};
        }

        .invalid-feedback {
            color: ${props => props.theme.colors.text.danger.color};
        }

        a {
           color: ${props => props.theme.colors.a.color};
            text-decoration: none !important;

            &:hover {
                text-decoration: underline !important;
            }

            &:focus, &:focus-visible {
                outline: none;
            }
        }
    }

    h1 {
        font-size: 18px !important;
        font-weight: 600 !important;
    }

    h2 {
        font-size: 17px !important;
        font-weight: 500 !important;
    }

    h3 {
        font-size: 16px !important;
        font-weight: 500 !important;
    }

    h4 {
        font-size: 15px !important;
        font-weight: 500 !important;
    }

    h5 {
        font-size: 14px !important;
        font-weight: 500 !important;
    }

    strong {
        font-weight: 500 !important;
    }

    .vr {
        background-color: ${props => props.theme.colors.body.color} !important;
        width: 2px !important;
    }

    .action-icon {
        &:hover {
            cursor: pointer;
        }
    }

    .add-to-filter {
        &:hover {
            cursor: zoom-in;
            text-decoration: underline;
        }
    }

    .add-to-filter-and-navigate {
        &:hover {
            cursor: pointer;
            text-decoration: underline;
        }
    }

    .cursor-pointer {
        cursor: pointer;
    }

    .cursor-not-allowed {
        cursor: not-allowed;
    }

    //PDF
    @media print {
        * {
            -webkit-print-color-adjust: exact;
            -webkit-font-smoothing: antialiased;
        }
    }

    @page  {
        margin: 15mm;
        size: A4; /*or width then height 150mm 50mm*/
    }

    .pdf {
        font-family: Arial !important;
        font-size: 16pt !important;
        color: ${props => props.theme.colors.pdf.textColor} !important;

        h1 {
            font-size: 24pt !important;
            font-weight: 600 !important;
        }

        h2 {
            font-size: 22pt !important;
            font-weight: 600 !important;
        }

        h3 {
            font-size: 20pt !important;
            font-weight: 600 !important;
        }

        h4 {
            font-size: 18pt !important;
            font-weight: 600 !important;
        }

        h5 {
            font-size: 16pt !important;
            font-weight: 500 !important;
        }

        .cover {
            font-size: 18pt !important;

            h1 {
                font-size: 34pt !important;
                font-weight: 600 !important;
            }

            h2 {
                font-size: 30pt !important;
                font-weight: 600 !important;
            }

            h3 {
                font-size: 22pt !important;
                font-weight: 600 !important;
            }

            h4 {
                font-size: 22pt !important;
                font-weight: 500 !important;
            }

            h5 {
                font-size: 20pt !important;
                font-weight: 500 !important;
            }
        }

        .table {
            th {
                color: ${props => props.theme.colors.pdf.textColor};
            }

            td {
                color: ${props => props.theme.colors.pdf.textColor};
            }
        }
    }

    // Font-awesome Overrides
    .svg-inline--fa {
        font-size: 16px !important;
        vertical-align: middle;
    }

    // Recharts
    .recharts-tooltip {
        background: ${props => props.theme.colors.charts.tooltip.backgroundColor};
        border: ${props => props.theme.colors.charts.tooltip.border};
        color: ${props => props.theme.colors.charts.tooltip.textColor};
        border-radius: 5px;
        padding: 10px 10px 5px 10px;
    }

    // Bootstrap Overrides
    .accordion {
        background: ${props => props.theme.colors.accordion.background} !important;
        color: ${props => props.theme.colors.accordion.color} !important;
        margin-bottom: 10px;

        &:focus-visible {
            outline: none;
        }

        .accordion-header {
            padding: 0px;

            &:focus-visible {
                outline: none;
            }

            button {
                &:focus-visible {
                    outline: none;
                }
            }

            .accordion-button {
                background: ${props => props.theme.colors.accordion.background} !important;
                color: ${props => props.theme.colors.accordion.color} !important;
                font-size: 15px !important;
                font-weight: 500 !important;

                &:not(.collapsed) {
                    background: ${props => props.theme.colors.accordion.header.open.background};
                    border: ${props => props.theme.colors.accordion.border};
                    color: ${props => props.theme.colors.accordion.header.open.color} !important;
                    box-shadow: none;
                }

                &.collapsed {
                    box-shadow: inset 0 -1px 0 rgb(0 0 0 / 13%);
                    &:focus-visible {
                        outline: none;
                    }
                }

                &:focus {
                    box-shadow: inset 0 -1px 0 rgb(0 0 0 / 13%);
                }

                &:hover {
                    background: ${props => props.theme.colors.accordion.header.open.background};
                    color: ${props => props.theme.colors.accordion.header.open.color} !important;
                }

                &::after {
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='${props =>
                        props.theme.colors.accordion
                            .iconColor}'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
                }
            }
        }

        .accordion-item {
            background: ${props => props.theme.colors.accordion.background} !important;
            border: ${props => props.theme.colors.accordion.border};

            &:focus-visible {
                outline: none;
            }
        }

        .accordion-collapse {
            background: ${props => props.theme.colors.accordion.background} !important;
            color: ${props => props.theme.colors.accordion.color} !important;

            &:focus-visible {
                outline: none;
            }
        }
    }

    .alert {
        &.alert-danger {
            background: ${props => props.theme.colors.alert.danger.background};
            border: ${props => props.theme.colors.alert.danger.border};
            color: ${props => props.theme.colors.alert.danger.color};
        }

        &.alert-primary {
            background: ${props => props.theme.colors.alert.primary.background};
            border: ${props => props.theme.colors.alert.primary.border};
            color: ${props => props.theme.colors.alert.primary.color};
        }

        &.alert-secondary {
            background: ${props => props.theme.colors.alert.secondary.background};
            border: ${props => props.theme.colors.alert.secondary.border};
            color: ${props => props.theme.colors.alert.secondary.color};
        }

        &.alert-success {
            background: ${props => props.theme.colors.alert.success.background};
            border: ${props => props.theme.colors.alert.success.border};
            color: ${props => props.theme.colors.alert.success.color};
        }

        &.alert-warning {
            background: ${props => props.theme.colors.alert.warning.background};
            border: ${props => props.theme.colors.alert.warning.border};
            color: ${props => props.theme.colors.alert.warning.color};
        }

        &.alert-info {
            background: ${props => props.theme.colors.alert.info.background};
            border: ${props => props.theme.colors.alert.info.border};
            color: ${props => props.theme.colors.alert.info.color};
        }
    }

    .badge {
        &.bg-danger {
            background: ${props => props.theme.colors.badge.danger.background} !important;
            color: ${props => props.theme.colors.badge.danger.color} !important;
        }

        &.bg-primary {
            background: ${props => props.theme.colors.badge.primary.background} !important;
            color: ${props => props.theme.colors.badge.primary.color} !important;
        }

        &.bg-secondary {
            background: ${props => props.theme.colors.badge.secondary.background} !important;
            color: ${props => props.theme.colors.badge.secondary.color} !important;
        }

        &.bg-success {
            background: ${props => props.theme.colors.badge.success.background} !important;
            color: ${props => props.theme.colors.badge.success.color} !important;
        }

        &.bg-warning {
            background: ${props => props.theme.colors.badge.warning.background} !important;
            color: ${props => props.theme.colors.badge.warning.color} !important;
        }

        &.bg-info {
            background: ${props => props.theme.colors.badge.info.background} !important;
            color: ${props => props.theme.colors.badge.info.color} !important;
        }
    }

    .dropdown-menu {
        font-size: 14px !important;
        background: ${props => props.theme.colors.dropdownMenu.background} !important;
        border: ${props => props.theme.colors.dropdownMenu.border} !important;
        color: ${props => props.theme.colors.dropdownMenu.color} !important;

        &.show {
            margin-top: 0px !important;
        }

        &.rbt-menu {
            background: ${props => props.theme.colors.dropdownMenu.background} !important;
        }

        a {
            background: ${props => props.theme.colors.dropdownMenu.background};
            color: ${props => props.theme.colors.dropdownMenu.color};

            &.active {
                cursor: default;
                background: ${props => props.theme.colors.dropdownMenu.active.background};
                color: ${props => props.theme.colors.dropdownMenu.active.color} !important;
                font-weight: ${props => props.theme.colors.dropdownMenu.active.fontWeight};

                &:hover {
                    background: ${props => props.theme.colors.dropdownMenu.active.background};
                    color: ${props => props.theme.colors.dropdownMenu.active.color} !important;
                    font-weight: ${props => props.theme.colors.dropdownMenu.active.fontWeight};
                }
            }

            &:hover {
                background: ${props => props.theme.colors.dropdownMenu.hover.background};
                color: ${props => props.theme.colors.dropdownMenu.hover.color} !important;
                font-weight: ${props => props.theme.colors.dropdownMenu.hover.fontWeight};
            }
        }
    }

    .list-group-item {
        background: ${props => props.theme.colors.listGroupItem.background} !important;
        border-color: ${props => props.theme.colors.listGroupItem.borderColor} !important;
        color: ${props => props.theme.colors.listGroupItem.color} !important;
    }

    .nav-pills {
        .nav-link {
            color: ${props => props.theme.colors.a.color} !important;

            &.active {
                background: ${props => props.theme.colors.navPills.background} !important;
                border-bottom: 2px solid ${props => props.theme.colors.a.color};
                border-radius: 0px;
                color: ${props => props.theme.colors.a.color} !important;
                font-weight: 600;
            }
        }
    }

    .navbar {
        &.navbar {
            background: ${props => props.theme.colors.nav.background};
            border-bottom: ${props => props.theme.colors.nav.borderBottom};
        }

        &.navbar-light {
            .navbar-nav {
                .nav-link {
                    color: ${props => props.theme.colors.nav.color};
                    font-size: 16px;
                    font-weight: 400;

                    &.nav-link:focus,
                    &.nav-link:hover {
                        color: ${props => props.theme.colors.nav.hover.color};
                    }

                    &.active {
                        border-bottom: ${props => props.theme.colors.nav.active.borderBottom};
                    }

                    &.disabled {
                        color: ${props => props.theme.colors.nav.disabled.color};
                    }
                }
            }
        }

        .navbar-toggler {
            color: ${props => props.theme.colors.nav.color} !important;
        }

        .dropdown-item {
            font-size: 14px;
            font-weight: 400;

            :focus, :hover {
                background: ${props => props.theme.colors.nav.active.background};
                color: ${props => props.theme.colors.nav.active.color};
            }

            &.active {
                background: ${props => props.theme.colors.nav.active.background};
                color: ${props => props.theme.colors.nav.active.color};
                font-weight: ${props => props.theme.colors.nav.active.fontWeight};
            }
        }

        .dropdown-menu {
            background: ${props => props.theme.colors.nav.dropdownMenu.background} !important;

            a {
                background: ${props => props.theme.colors.nav.dropdownMenu.background} !important;
                color: ${props => props.theme.colors.nav.dropdownMenu.color} !important;

                &.active {
                    cursor: default;
                    background: ${props => props.theme.colors.nav.dropdownMenu.active.background};
                    color: ${props => props.theme.colors.nav.dropdownMenu.active.color} !important;
                    font-weight: ${props => props.theme.colors.nav.dropdownMenu.active.fontWeight};

                    &:hover {
                        background: ${props => props.theme.colors.nav.dropdownMenu.active.background};
                        color: ${props => props.theme.colors.nav.dropdownMenu.active.color} !important;
                        font-weight: ${props => props.theme.colors.nav.dropdownMenu.active.fontWeight};
                    }
                }

                &:hover {
                    background: ${props => props.theme.colors.nav.dropdownMenu.hover.background};
                    color: ${props => props.theme.colors.nav.dropdownMenu.hover.color} !important;
                    font-weight: ${props => props.theme.colors.nav.dropdownMenu.hover.fontWeight};
                }
            }
        }
    }

    .progress-bar {
        background-color: ${props => props.theme.colors.progressBar.backgroundColor};
        color: ${props => props.theme.colors.progressBar.color};
    }

    .card {
        background: ${props => props.theme.colors.card.background} !important;
        /* Bootstrap 5.3's .card carries its own color: var(--bs-body-color), which is its
           light-theme near-black. That is a direct declaration at the same specificity as
           this one, so without !important it wins and every heading, form label and bare
           text node inside a card renders near-black on the dark card background -- the
           same trap .modal-content falls into below. The theme's card.color is correct;
           it just has to be the declaration that lands. */
        color: ${props => props.theme.colors.card.color} !important;
        box-shadow: ${props => props.theme.colors.card.boxShadow};
        border: ${props => props.theme.colors.card.border} !important;
        border-radius: ${props => props.theme.colors.card.borderRadius} !important;
        padding: 0rem 1rem;
        margin-bottom: 1rem;

        .card-header {
            background: ${props => props.theme.colors.card.header.background};
            border-bottom: ${props => props.theme.colors.card.header.borderBottom};
            color: ${props => props.theme.colors.card.header.color};
            padding: 1.5rem 0.75rem;

            .card-title {
                font-size: 15px !important;
                font-weight: 500 !important;
                margin-bottom: 0rem;
            }
        }

        .card-body {
            padding: 1.25rem 0.75rem;
        }

        .card-footer {
            padding: 1rem 0.75rem;
            background: ${props => props.theme.colors.card.footer.background};
            border-top: ${props => props.theme.colors.card.footer.borderTop};
            color: ${props => props.theme.colors.card.footer.color};
        }
    }

    .dropdown-toggle::after {
        display: none !important;
    }

    .table {
        // Bootstrap 5.3 defaults --bs-table-bg to --bs-body-bg (white) and paints
        // each cell with it via a box-shadow inset, which hides the themed table
        // background below and makes cells unreadable in dark themes. Reset the cell
        // background/color to the theme so the table background shows through.
        --bs-table-bg: transparent;
        --bs-table-color: ${props => props.theme.colors.table.color};
        background: ${props => props.theme.colors.table.background};
        border-color: ${props => props.theme.colors.table.borderColor} !important;
        border-collapse: collapse;
        border-width: 1px;

        th {
             font-weight: 500 !important;
             color: ${props => props.theme.colors.table.color};
        }

        td {
            color: ${props => props.theme.colors.table.color};
        }

        &.table-hover>tbody>tr:hover>* {
            background-color: ${props => props.theme.colors.table.hover.backgroundColor};
            color: ${props => props.theme.colors.table.hover.color};
        }
    }

    .table-striped>tbody>tr:nth-of-type(odd)>* {
        color: ${props => props.theme.colors.table.color} !important;
    }

    .table-responsive::-webkit-scrollbar {
        -webkit-appearance: none;
    }

    .table-responsive::-webkit-scrollbar:vertical {
        width: 12px;
    }

    .table-responsive::-webkit-scrollbar:horizontal {
        height: 12px;
    }

    .table-responsive::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, .5);
        border-radius: 10px;
        border: 2px solid #ffffff;
    }

    .table-responsive::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: #ffffff;
    }

    .btn {
        font-size: 14px !important;

        &:hover {
            opacity: .95;
        }

        &:active {
            outline: 0;
        }

        &.btn-light {
            color: ${props => props.theme.colors.button.light.color} !important;
        }

        &.btn-primary {
            background: ${props => props.theme.colors.button.primary.background} !important;
            border: ${props => props.theme.colors.button.primary.border} !important;
            box-shadow: ${props => props.theme.colors.button.primary.boxShadow} !important;
            box-sizing: border-box;
            color: ${props => props.theme.colors.button.primary.color} !important;

            &:hover {
                background: ${props => props.theme.colors.button.primary.hover.background} !important;
                border: ${props => props.theme.colors.button.primary.hover.border} !important;
                color: ${props => props.theme.colors.button.primary.hover.color} !important;
                box-shadow: ${props => props.theme.colors.button.primary.hover.boxShadow} !important;
            }
        }

        /* Outline primary wears the buttonGroup tokens rather than a set of its
           own: a group item at rest is already "outlined in the brand colour,
           fills on hover", which is exactly this button. Without this rule the
           variant falls through to Bootstrap's own $primary and renders blue on
           every theme. */
        &.btn-outline-primary {
            background: ${props => props.theme.colors.buttonGroup.primary.background} !important;
            border: ${props => props.theme.colors.buttonGroup.primary.border} !important;
            box-sizing: border-box;
            color: ${props => props.theme.colors.buttonGroup.primary.color} !important;

            &:hover {
                background: ${props => props.theme.colors.buttonGroup.primary.hover.background} !important;
                border: ${props => props.theme.colors.buttonGroup.primary.hover.border} !important;
                color: ${props => props.theme.colors.buttonGroup.primary.hover.color} !important;
                box-shadow: ${props => props.theme.colors.buttonGroup.primary.hover.boxShadow} !important;
            }

            /* Bootstrap's disabled fill would read as a pressed button, so this
               keeps the resting look and only drops it back. */
            &:disabled {
                background: ${props => props.theme.colors.buttonGroup.primary.background} !important;
                border: ${props => props.theme.colors.buttonGroup.primary.border} !important;
                color: ${props => props.theme.colors.buttonGroup.primary.color} !important;
                opacity: .55;
            }
        }

        &.btn-secondary {
            background: ${props => props.theme.colors.button.secondary.background} !important;
            border: ${props => props.theme.colors.button.secondary.border} !important;
            box-shadow: ${props => props.theme.colors.button.secondary.boxShadow} !important;
            box-sizing: border-box;
            color: ${props => props.theme.colors.button.secondary.color} !important;

            &:hover {
                background: ${props => props.theme.colors.button.secondary.hover.background} !important;
                border: ${props => props.theme.colors.button.secondary.hover.border} !important;
                color: ${props => props.theme.colors.button.secondary.hover.color} !important;
                box-shadow: ${props => props.theme.colors.button.secondary.hover.boxShadow} !important;
            }

            &.active {
                background: ${props => props.theme.colors.button.secondary.active.background} !important;
                border: ${props => props.theme.colors.button.secondary.active.border} !important;
                color: ${props => props.theme.colors.button.secondary.active.color} !important;
                box-shadow: ${props => props.theme.colors.button.secondary.active.boxShadow} !important;
                cursor: default;
            }
        }

        &.btn-danger {
            background: ${props => props.theme.colors.button.danger.background} !important;
            border: ${props => props.theme.colors.button.danger.border} !important;
            box-shadow: ${props => props.theme.colors.button.danger.boxShadow} !important;
            box-sizing: border-box;
            color: ${props => props.theme.colors.button.danger.color} !important;

            &:hover {
                background: ${props => props.theme.colors.button.danger.hover.background} !important;
                border: ${props => props.theme.colors.button.danger.hover.border} !important;
                color: ${props => props.theme.colors.button.danger.hover.color} !important;
                box-shadow: ${props => props.theme.colors.button.danger.hover.boxShadow} !important;
            }
        }
    }

    .btn-group>.btn-group:not(:last-child)>.btn, .btn-group>.btn:not(:last-child):not(.dropdown-toggle) {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
    }

    .dropdown-item.active,
    .dropdown-item:active {
        background: ${props => props.theme.colors.dropdown.button.secondary.active.background} !important;
        color: ${props => props.theme.colors.dropdown.button.secondary.active.color} !important;
    }

    /* background-color, not the background shorthand: a checked switch carries
       its knob as a background-image, and the shorthand would drop it. The
       formCheck token was defined in every theme but read by nothing, so until
       now every checkbox and switch in the product rendered Bootstrap blue. */
    .form-check-input:checked {
        background-color: ${props => props.theme.colors.formCheck.background} !important;
        border-color: ${props => props.theme.colors.formCheck.border} !important;
    }

    .form-check-input:focus {
        box-shadow: ${props => props.theme.colors.formCheck.focus.boxShadow} !important;
    }

    .form-control {
        background-color: ${props => props.theme.colors.formControl.backgroundColor} !important;
        color: ${props => props.theme.colors.formControl.color} !important;
        border: ${props => props.theme.colors.formControl.border} !important;
        font-size: 14px !important;

        /* Bootstrap's placeholder colour comes from --bs-secondary-color, a fixed dark
           grey chosen for a light background. On a dark input it lands at roughly 1.5:1
           and the placeholder is effectively invisible -- which on the login form means
           two unlabelled boxes. Derive it from the theme's own field colour instead, so
           it is always a dimmed version of readable text in either variant. */
        &::placeholder {
            color: ${props => props.theme.colors.formControl.color} !important;
            opacity: 0.6;
        }

        &:disabled {
            background-color: ${props => props.theme.colors.formControl.disabled.backgroundColor} !important;
        }

        &:focus {
            box-shadow: ${props => props.theme.colors.formControl.focus.boxShadow} !important;
        }

        &.is-valid {
            box-shadow: ${props => props.theme.colors.formControl.isValid.boxShadow} !important;
        }

        &.is-invalid {
            box-shadow: ${props => props.theme.colors.formControl.isInvalid.boxShadow} !important;
        }
    }

    .form-select {
        background-color: ${props => props.theme.colors.formControl.backgroundColor} !important;
        color: ${props => props.theme.colors.formControl.color} !important;
        border: ${props => props.theme.colors.formControl.border} !important;

        &:disabled {
            background-color: ${props => props.theme.colors.formControl.disabled.backgroundColor} !important;
        }
    }

    .input-group-text {
        background: ${props => props.theme.colors.inputGroup.background} !important;
        color: ${props => props.theme.colors.inputGroup.color} !important;
        border: ${props => props.theme.colors.inputGroup.border} !important;
    }

    .tab-content {
        background: ${props => props.theme.colors.tabs.background};
        border-top: ${props => props.theme.colors.tabs.borderTop};
        border-right: ${props => props.theme.colors.tabs.borderRight};
        border-bottom: ${props => props.theme.colors.tabs.borderBottom};
        border-left: ${props => props.theme.colors.tabs.borderLeft};
        box-shadow: ${props => props.theme.colors.tabs.boxShadow};
        border-radius: ${props => props.theme.colors.tabs.borderRadius};
        color: ${props => props.theme.colors.tabs.color};
    }

    .modal {
        color: ${props => props.theme.colors.modal.color};

        /* Bootstrap's .modal-content sets its own color from --bs-modal-color (which chains to
           --bs-body-color, Bootstrap's light-theme default of near-black). That is a direct
           declaration on a descendant, so it wins over the color set above on .modal regardless
           of !important -- an ancestor's color is only ever an inheritance fallback, and
           .modal-content never falls back because Bootstrap gives it an explicit value. Every
           dark theme ends up with unreadable near-black text on any plain (unclassed) modal
           content as a result. Re-declaring color here, directly on .modal-content, is what
           actually reaches it. */
        .modal-content {
            color: ${props => props.theme.colors.modal.color} !important;
        }

        .modal-header {
            background: ${props => props.theme.colors.modal.background};
            border: ${props => props.theme.colors.modal.border};
            border-bottom: none;

            .modal-title {
                font-size: 16px !important;
                font-weight: 500 !important;
            }
        }

        .modal-body {
            background: ${props => props.theme.colors.modal.background};
            border: ${props => props.theme.colors.modal.border};
            border-bottom: none;
        }

        .modal-footer {
            background: ${props => props.theme.colors.modal.background};
            border: ${props => props.theme.colors.modal.border};
        }
    }

    .nav-tabs {
        border-bottom: none !important;

        .nav-link {
            color: ${props => props.theme.colors.a.color} !important;

            &.active {
                border: ${props => props.theme.colors.tabs.active.border} !important;
                border-bottom: none !important;
                background: ${props => props.theme.colors.tabs.active.background} !important;
                color: ${props => props.theme.colors.tabs.active.color} !important;
            }
        }
    }

    .popover {
        background: ${props => props.theme.colors.popover.background} !important;
        color: ${props => props.theme.colors.popover.color};
        border: ${props => props.theme.colors.popover.border} !important;
        white-space: nowrap !important;
        max-width: fit-content !important;

        .popover-header {
            background: ${props => props.theme.colors.popover.background};
            border-bottom: ${props => props.theme.colors.popover.header.borderBottom};
            color: ${props => props.theme.colors.popover.color};
            font-size: 15px !important;
            font-weight: 500 !important;
        }

        .popover-body {
            background: ${props => props.theme.colors.popover.background};
            color: ${props => props.theme.colors.popover.color};
        }
    }

    // D3 Tip
    .d3-tip {
        background: ${props => props.theme.colors.charts.tooltip.backgroundColor};
        border: ${props => props.theme.colors.charts.tooltip.border};
        color: ${props => props.theme.colors.charts.tooltip.textColor};
        border-radius: 5px;
        padding: 15px;
    }
`
